/**
 * UTM campaign personalization.
 *
 * Reads ?utm_mode=<id> from the URL, remembers it in a cookie for as many
 * days as configured in theme settings (settings.utm_campaigns_config,
 * injected as window.utmCampaignsConfig), and toggles/reorders any elements
 * on the page carrying the data-utm-active / data-utm-change / data-utm-reorder
 * attributes to match the active campaign.
 *
 * Visitors without a personal UTM campaign fall back to the default campaigns
 * (settings.default_campaigns_config, injected as window.defaultCampaignsConfig),
 * each of which runs site-wide between its start and end date.
 *
 * ?utm_mode=balanced is a reserved link that opts the visitor out of both: it
 * replaces whatever campaign they had (and suppresses the site-wide default
 * ones) so the page shows its plain "default" state. Like any other campaign it
 * is remembered in the cookie, for `balanced: <days>` from theme settings if
 * present, otherwise for BALANCED_MODE_DAYS.
 *
 * Every element with data-utm-active is hidden by default via CSS
 * ([data-utm-active] { display: none; } in cart-sidebar.css). This script
 * removes the data-utm-active attribute from whichever element should be
 * shown, which is what makes it visible again.
 *
 * Markup contract:
 *   data-utm-active="hero_title"        groups elements that vary by campaign
 *   data-utm-change="sleep, default"    shows this element for these campaigns
 *                                        ("default" = shown when no campaign is active)
 *   data-utm-reorder="2, sleep"         this element becomes position 2 among its
 *                                        data-utm-active siblings when "sleep" is active;
 *                                        multiple rules are separated by ";", e.g.
 *                                        data-utm-reorder="2, sleep; 3, winter_blues"
 * Example:
 *   data-utm-active='hero_button' data-utm-change='sleep'
 *
 * Example:
 *   data-utm-active='hero_button' data-utm-reorder='2, sleep'
 *
 */

const COOKIE_NAME = 'utm_campaign';
// Reserved campaign id that means "no campaign at all", see the note above.
const BALANCED_MODE = 'balanced';
const BALANCED_MODE_DAYS = 30;

function getUrlCampaign() {
  const value = new URLSearchParams(window.location.search).get('utm_mode');
  return value ? value.trim().toLowerCase() : null;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name, value, expiresDate) {
  document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; expires=' + expiresDate.toUTCString();
}

function deleteCookie(name) {
  document.cookie = name + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
}

// "sleep: 30\nwinter_blues: 14" -> { sleep: 30, winter_blues: 14 } (duration in days)
function parseCampaignsConfig(raw) {
  const config = {};
  (raw || '').split('\n').forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.indexOf('#') === 0) return;

    const separatorIndex = trimmed.indexOf(':');
    if (separatorIndex === -1) return;

    const id = trimmed.slice(0, separatorIndex).trim().toLowerCase();
    const days = parseFloat(trimmed.slice(separatorIndex + 1).trim());

    if (id && !isNaN(days) && days > 0) {
      config[id] = days;
    }
  });
  return config;
}

function daysFromNow(days) {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000);
}

function resolveActiveCampaign(campaignsConfig) {
  const urlCampaign = getUrlCampaign();

  // Following a campaign link (re)starts the countdown for this visitor.
  if (urlCampaign === BALANCED_MODE) {
    setCookie(COOKIE_NAME, BALANCED_MODE, daysFromNow(campaignsConfig[BALANCED_MODE] || BALANCED_MODE_DAYS));
    return BALANCED_MODE;
  }

  if (urlCampaign && campaignsConfig[urlCampaign]) {
    setCookie(COOKIE_NAME, urlCampaign, daysFromNow(campaignsConfig[urlCampaign]));
    return urlCampaign;
  }

  // The cookie expires on its own once the configured duration runs out.
  const activeCampaign = getCookie(COOKIE_NAME);
  if (!activeCampaign) return null;

  if (activeCampaign !== BALANCED_MODE && !campaignsConfig[activeCampaign]) {
    deleteCookie(COOKIE_NAME);
    return null;
  }

  return activeCampaign;
}

// "2026-09-01 -> sleep -> 2026-09-30" -> [{ id: 'sleep', startDate: Date, endDate: Date }]
function parseDefaultCampaignsConfig(raw) {
  const campaigns = [];
  (raw || '').split('\n').forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.indexOf('#') === 0) return;

    const parts = trimmed.split('->').map((part) => part.trim());
    if (parts.length < 3) return;

    const id = parts[1].toLowerCase();
    if (!id) return;

    const startDate = new Date(parts[0] + 'T00:00:00');
    const endDate = new Date(parts[2] + 'T23:59:59');
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;

    campaigns.push({ id: id, startDate: startDate, endDate: endDate });
  });
  return campaigns;
}

function resolveDefaultCampaign(defaultCampaigns) {
  const now = new Date();
  const active = defaultCampaigns.find((campaign) => campaign.startDate <= now && campaign.endDate >= now);
  return active ? active.id : null;
}

function parseChangeList(el) {
  return (el.getAttribute('data-utm-change') || '')
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

function parseReorderPosition(el, campaign) {
  const raw = el.getAttribute('data-utm-reorder');
  if (!raw || !campaign) return null;

  const rules = raw.split(';');
  const matchedRule = rules
    .map((rule) => {
      const parts = rule.split(',');
      return {
        position: parseInt(parts[0], 10),
        campaign: (parts[1] || '').trim().toLowerCase(),
      };
    })
    .find((rule) => rule.campaign === campaign && !isNaN(rule.position));

  return matchedRule ? matchedRule.position : null;
}

function applyVisibility(elements, campaign) {
  const withChange = elements.filter((el) => el.hasAttribute('data-utm-change'));

  let matched = withChange.filter((el) => campaign && parseChangeList(el).includes(campaign));

  if (!matched.length) {
    matched = withChange.filter((el) => parseChangeList(el).includes('default'));
  }

  elements.forEach((el) => {
    if (!el.hasAttribute('data-utm-change') || matched.includes(el)) {
      el.removeAttribute('data-utm-active');
    }
  });
}

function reorderGroup(parent, elements, campaign) {
  if (!campaign || elements.length < 2) return;

  const withPosition = [];
  const withoutPosition = [];
  elements.forEach((el) => {
    const position = parseReorderPosition(el, campaign);
    if (position != null) {
      withPosition.push({ el, position });
    } else {
      withoutPosition.push(el);
    }
  });

  if (!withPosition.length) return;

  const total = elements.length;
  const finalOrder = new Array(total).fill(null);
  withPosition.forEach((entry) => {
    const index = Math.min(Math.max(entry.position - 1, 0), total - 1);
    if (!finalOrder[index]) {
      finalOrder[index] = entry.el;
    } else {
      finalOrder.push(entry.el);
    }
  });

  let nextFiller = 0;
  finalOrder.forEach((slot, index) => {
    if (!slot) finalOrder[index] = withoutPosition[nextFiller++];
  });
  while (nextFiller < withoutPosition.length) {
    finalOrder.push(withoutPosition[nextFiller++]);
  }

  // Anchor the reorder to where the group currently lives so unrelated
  // siblings in the same parent keep their position.
  const marker = document.createComment('utm-reorder-marker');
  parent.insertBefore(marker, elements[0]);
  finalOrder.filter(Boolean).forEach((el) => {
    parent.insertBefore(el, marker);
  });
  marker.remove();
}

function applyUtmCampaign(campaign) {
  if (campaign) {
    document.documentElement.setAttribute('data-utm-campaign', campaign);
  } else {
    document.documentElement.removeAttribute('data-utm-campaign');
  }
  
  const groups = {};
  document.querySelectorAll('[data-utm-active]').forEach((el) => {
    const type = el.getAttribute('data-utm-active').trim();
    if (!type) return;
    if (!groups[type]) groups[type] = [];
    groups[type].push(el);
  });

  Object.keys(groups).forEach((type) => {
    const elements = groups[type];

    applyVisibility(elements, campaign);

    const byParent = new Map();
    elements.forEach((el) => {
      const parent = el.parentElement;
      if (!parent) return;
      if (!byParent.has(parent)) byParent.set(parent, []);
      byParent.get(parent).push(el);
    });
    byParent.forEach((siblings, parent) => {
      reorderGroup(parent, siblings, campaign);
    });
  });
}

function isThemeEditor() {
  return Boolean(window.Shopify && window.Shopify.designMode);
}

function init() {
  // In the Shopify theme editor no campaign is ever active: the cookie is not
  // read or written and nothing is reordered, so the merchant always edits the
  // "default" state of the page.
  if (isThemeEditor()) {
    applyUtmCampaign(null);
    return;
  }

  const campaignsConfig = parseCampaignsConfig(window.utmCampaignsConfig);
  const defaultCampaigns = parseDefaultCampaignsConfig(window.defaultCampaignsConfig);
  // A personal UTM campaign always wins over the site-wide default one.
  const activeCampaign = resolveActiveCampaign(campaignsConfig) || resolveDefaultCampaign(defaultCampaigns);
  // "balanced" is what suppresses the default campaigns above; from here on it
  // is just the absence of a campaign.
  applyUtmCampaign(activeCampaign === BALANCED_MODE ? null : activeCampaign);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Sections re-rendered by the editor come back with data-utm-active in place,
// so they would stay hidden by CSS until the default state is applied again.
if (isThemeEditor()) {
  document.addEventListener('shopify:section:load', function () {
    applyUtmCampaign(null);
  });
}
